import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Check, ChevronLeft } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero, PageShell, Reveal } from "@/components/ui/page";
import { Band, H2, LINK_ACCENT } from "@/components/ui/lv";
import { faqPageSchema, type FAQItemInput } from "@/lib/structuredData";

export interface GuideSection {
  title: string;
  body: ReactNode;
}

interface GuidePageProps {
  title: string;
  seoTitle?: string;
  h1Title?: string;
  description: string;
  keywords: string;
  badge: string;
  answer: ReactNode;
  sections: GuideSection[];
  faq: FAQItemInput[];
  canonicalPath: string;
  updated: string;
  updatedIso: string;
  related: Array<{ label: string; to: string }>;
  /** Hero illustration; see data/guideArt.ts. */
  art?: string;
}

export function GuidePage(props: GuidePageProps) {
  const article = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: props.h1Title ?? props.seoTitle ?? props.title,
    description: props.description,
    inLanguage: "fa-IR",
    dateModified: props.updatedIso,
    author: { "@type": "Organization", name: "گردو کلاد", url: "https://gerdoo.cloud" },
    publisher: { "@type": "Organization", name: "گردو کلاد", url: "https://gerdoo.cloud" },
    mainEntityOfPage: `https://gerdoo.cloud${props.canonicalPath}`,
  };

  return (
    <PageShell className="lv-home selection:bg-gerdoo-500/20">
      <SEO
        title={props.seoTitle ?? props.title}
        description={props.description}
        keywords={props.keywords}
        canonicalPath={props.canonicalPath}
        markdownPath={`${props.canonicalPath}.md`}
        ogType="article"
        jsonLd={[article, faqPageSchema(props.faq)]}
      />
      <PageHero
        badge={props.badge}
        badgeIcon={BookOpen}
        title={props.h1Title ?? props.title}
        subtitle={props.description}
        art={props.art}
      />

      <Band className="pt-0 sm:pt-0">
        <Reveal className="mx-auto max-w-[760px]">
          <article className="lv-card p-7 text-start sm:p-9">
            <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-gerdoo-700">
              <Check className="h-4 w-4" aria-hidden />
              پاسخ کوتاه
            </span>
            <div className="mt-4 text-[16px] font-medium leading-[2] text-ink-900 sm:text-[17px]">
              {props.answer}
            </div>
            <p className="mt-5 text-[12.5px] text-ink-500">آخرین بازبینی: {props.updated}</p>
          </article>
        </Reveal>
      </Band>

      <Band className="pt-0 sm:pt-0">
        <div className="mx-auto max-w-[760px]">
          {props.sections.map((section) => (
            <Reveal key={section.title} className="border-t border-black/[0.08] py-12 text-start first:border-t-0 first:pt-0 sm:py-14">
              <h2 className={H2}>{section.title}</h2>
              <div className="mt-6 space-y-5 text-[15px] leading-[2] text-ink-700 sm:text-[16px] [&_:not(pre)>code]:rounded [&_:not(pre)>code]:bg-gerdoo-50/70 [&_:not(pre)>code]:px-1.5 [&_:not(pre)>code]:py-0.5 [&_:not(pre)>code]:font-mono [&_:not(pre)>code]:text-[13px] [&_a]:font-bold [&_a]:text-gerdoo-700 [&_li]:mb-2 [&_ol]:list-decimal [&_ol]:pr-6 [&_strong]:text-ink-950 [&_ul]:list-disc [&_ul]:pr-6">
                {section.body}
              </div>
            </Reveal>
          ))}
        </div>
      </Band>

      <Band className="pt-0 sm:pt-0">
        <div className="mx-auto max-w-[760px] text-start">
          <h2 className={H2}>سوالات پرتکرار</h2>
          <div className="mt-8 border-t border-black/[0.08]">
            {props.faq.map((item, i) => (
              <Reveal key={item.q} i={i}>
                <article className="border-b border-black/[0.08] py-7">
                  <h3 className="text-[16px] font-bold leading-[1.8] tracking-tight text-ink-950">
                    {item.q}
                  </h3>
                  <p className="mt-2 text-[15px] leading-[2] text-ink-600">{item.a}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <nav aria-label="مطالب مرتبط" className="mt-10 flex flex-wrap gap-x-7 gap-y-3">
            {props.related.map((item) => (
              <Link key={item.to} to={item.to} className={LINK_ACCENT}>
                {item.label}
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </Link>
            ))}
          </nav>
        </div>
      </Band>
    </PageShell>
  );
}
